import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Mail, Inbox, Send, Star, Trash2, Archive, RefreshCw, Search, Plus,
  MoreVertical, Paperclip, Reply, User, Building2, X, Loader2,
  Folder, AlertCircle, CheckCircle2, Download, Edit, MailPlus,
  Link2, Unlink, Sparkles, Bot, ThumbsUp, ThumbsDown, Briefcase, Wand2
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDER_CONFIG = {
  gmail: { icon: '📧', color: 'text-red-500', bg: 'bg-red-50' },
  microsoft: { icon: '📨', color: 'text-blue-500', bg: 'bg-blue-50' },
  imap: { icon: '📬', color: 'text-gray-500', bg: 'bg-gray-50' },
  mock: { icon: '✉️', color: 'text-purple-500', bg: 'bg-purple-50' }
};

const FOLDER_ICONS = {
  INBOX: <Inbox className="h-4 w-4" />,
  SENT: <Send className="h-4 w-4" />,
  DRAFTS: <Edit className="h-4 w-4" />,
  STARRED: <Star className="h-4 w-4" />,
  TRASH: <Trash2 className="h-4 w-4" />,
  ARCHIVE: <Archive className="h-4 w-4" />
};

const EmailHub = () => {
  const { user } = useAuth();
  
  const [accounts, setAccounts] = useState([]);
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [stats, setStats] = useState({});
  
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [selectedFolder, setSelectedFolder] = useState('INBOX');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterUnread, setFilterUnread] = useState(false);
  const [filterStarred, setFilterStarred] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showComposeModal, setShowComposeModal] = useState(false);
  
  const [composeData, setComposeData] = useState({
    to: '', cc: '', bcc: '', subject: '', body: '',
    accountId: '', replyToThreadId: null, isReply: false, isForward: false
  });
  
  const [connectForm, setConnectForm] = useState({
    provider: 'gmail', email: '', displayName: ''
  });
  
  // AI and Linking state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showAIDraftModal, setShowAIDraftModal] = useState(false);
  const [linkableContacts, setLinkableContacts] = useState([]);
  const [linkableJobs, setLinkableJobs] = useState([]);
  const [linkSearch, setLinkSearch] = useState('');
  const [selectedLinkContact, setSelectedLinkContact] = useState(null);
  const [selectedLinkJob, setSelectedLinkJob] = useState(null);
  const [linking, setLinking] = useState(false);
  
  // AI Draft state
  const [aiDraftInstructions, setAiDraftInstructions] = useState('');
  const [aiDraftTone, setAiDraftTone] = useState('professional');
  const [generatingDraft, setGeneratingDraft] = useState(false);
  const [aiDraft, setAiDraft] = useState(null);
  const [pendingDrafts, setPendingDrafts] = useState([]);
  const [showPendingDrafts, setShowPendingDrafts] = useState(false);

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/email/accounts`, { params: { user_id: user.id } });
      setAccounts(res.data.accounts || []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  }, [user?.id]);

  const fetchThreads = useCallback(async () => {
    if (!user?.id) return;
    try {
      const params = { user_id: user.id, folder: selectedFolder, page: 1, page_size: 50 };
      if (selectedAccount !== 'all') params.account_id = selectedAccount;
      if (filterUnread) params.is_unread = true;
      if (filterStarred) params.is_starred = true;
      if (searchQuery) params.search = searchQuery;
      
      const res = await axios.get(`${API}/email/inbox`, { params });
      setThreads(res.data.threads || []);
    } catch (error) {
      console.error('Error fetching threads:', error);
    }
  }, [user?.id, selectedAccount, selectedFolder, filterUnread, filterStarred, searchQuery]);

  const fetchStats = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API}/email/stats`, { params: { user_id: user.id } });
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [user?.id]);

  const fetchThread = async (threadId) => {
    setLoadingThread(true);
    try {
      const res = await axios.get(`${API}/email/threads/${threadId}`, { params: { user_id: user.id } });
      setSelectedThread(res.data.thread);
      setThreadMessages(res.data.messages || []);
      setThreads(prev => prev.map(t => t.id === threadId ? { ...t, is_unread: false } : t));
    } catch (error) {
      toast.error('Failed to load email thread');
    } finally {
      setLoadingThread(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAccounts(), fetchStats()]);
      setLoading(false);
    };
    if (user?.id) loadData();
  }, [user?.id, fetchAccounts, fetchStats]);

  useEffect(() => {
    if (user?.id && accounts.length > 0) fetchThreads();
  }, [user?.id, accounts.length, selectedAccount, selectedFolder, filterUnread, filterStarred, searchQuery, fetchThreads]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/email/sync`, null, { params: { user_id: user.id } });
      toast.success(`Synced ${res.data.synced_accounts?.length || 0} account(s)`);
      await fetchThreads();
      await fetchStats();
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleConnect = async () => {
    if (!connectForm.email) { toast.error('Please enter an email address'); return; }
    try {
      await axios.post(`${API}/email/accounts/connect`, null, {
        params: { user_id: user.id, provider: connectForm.provider, email_address: connectForm.email, display_name: connectForm.displayName || null }
      });
      toast.success(`Connected ${connectForm.email}`);
      setShowConnectModal(false);
      setConnectForm({ provider: 'gmail', email: '', displayName: '' });
      await fetchAccounts();
      await fetchThreads();
      await fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect account');
    }
  };

  const handleDisconnect = async (accountId) => {
    if (!window.confirm('Disconnect this account?')) return;
    try {
      await axios.delete(`${API}/email/accounts/${accountId}`, { params: { user_id: user.id } });
      toast.success('Account disconnected');
      await fetchAccounts();
      await fetchThreads();
      await fetchStats();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleStar = async (threadId, starred) => {
    try {
      await axios.post(`${API}/email/threads/${threadId}/star`, null, { params: { user_id: user.id, starred } });
      setThreads(prev => prev.map(t => t.id === threadId ? { ...t, is_starred: starred } : t));
      if (selectedThread?.id === threadId) setSelectedThread(prev => ({ ...prev, is_starred: starred }));
    } catch (error) {
      toast.error('Failed to update star');
    }
  };

  const handleSend = async () => {
    if (!composeData.to || !composeData.subject || !composeData.body) {
      toast.error('Please fill in all required fields'); return;
    }
    const accountId = composeData.accountId || accounts[0]?.id;
    if (!accountId) { toast.error('No email account selected'); return; }
    
    setSending(true);
    try {
      await axios.post(`${API}/email/send-direct`, null, {
        params: {
          user_id: user.id, account_id: accountId,
          to: composeData.to.split(',').map(e => e.trim()),
          subject: composeData.subject,
          body_html: `<p>${composeData.body.replace(/\n/g, '</p><p>')}</p>`,
          body_text: composeData.body,
          cc: composeData.cc ? composeData.cc.split(',').map(e => e.trim()) : [],
          reply_to_thread_id: composeData.replyToThreadId
        }
      });
      toast.success('Email sent!');
      setShowComposeModal(false);
      setComposeData({ to: '', cc: '', bcc: '', subject: '', body: '', accountId: '', replyToThreadId: null, isReply: false, isForward: false });
      await fetchThreads();
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleReply = (thread, message) => {
    const replyTo = message?.from_participant?.email || thread?.participants?.[0]?.email;
    setComposeData({
      to: replyTo || '', cc: '', bcc: '',
      subject: `Re: ${thread?.subject || message?.subject || ''}`,
      body: `\n\n---\nOn ${new Date(message?.date || thread?.last_message_date).toLocaleString()}, ${message?.from_participant?.name || replyTo} wrote:\n> ${message?.snippet || thread?.snippet || ''}`,
      accountId: thread?.account_id || accounts[0]?.id,
      replyToThreadId: thread?.id, isReply: true, isForward: false
    });
    setShowComposeModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    if (diff < 86400000 && date.getDate() === now.getDate()) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diff < 604800000) return date.toLocaleDateString([], { weekday: 'short' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getInitials = (name, email) => {
    if (name) return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    return email?.slice(0, 2).toUpperCase() || '??';
  };

  // ========== Linking Functions ==========
  
  const fetchLinkableContacts = async (search = '') => {
    try {
      const res = await axios.get(`${API}/email/linkable/contacts`, { params: { search, limit: 20 } });
      setLinkableContacts(res.data.contacts || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchLinkableJobs = async (contactId = null, search = '') => {
    try {
      const res = await axios.get(`${API}/email/linkable/jobs`, { params: { contact_id: contactId, search, limit: 20 } });
      setLinkableJobs(res.data.jobs || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const openLinkModal = async () => {
    setShowLinkModal(true);
    setSelectedLinkContact(null);
    setSelectedLinkJob(null);
    setLinkSearch('');
    await fetchLinkableContacts();
    await fetchLinkableJobs();
  };

  const handleManualLink = async () => {
    if (!selectedThread) return;
    if (!selectedLinkContact && !selectedLinkJob) {
      toast.error('Please select a contact or job to link');
      return;
    }
    
    setLinking(true);
    try {
      await axios.post(`${API}/email/link`, {
        thread_id: selectedThread.id,
        contact_id: selectedLinkContact?.id || null,
        job_id: selectedLinkJob?.id || null
      });
      
      toast.success('Email linked successfully');
      setShowLinkModal(false);
      
      // Update local state
      setSelectedThread(prev => ({
        ...prev,
        linked_contact_id: selectedLinkContact?.id || prev?.linked_contact_id,
        linked_contact_name: selectedLinkContact ? `${selectedLinkContact.first_name} ${selectedLinkContact.last_name}` : prev?.linked_contact_name,
        linked_job_id: selectedLinkJob?.id || prev?.linked_job_id,
        linked_job_title: selectedLinkJob?.title || prev?.linked_job_title,
        link_confirmed: true
      }));
      
      // Update in thread list
      setThreads(prev => prev.map(t => t.id === selectedThread.id ? {
        ...t,
        linked_contact_id: selectedLinkContact?.id || t.linked_contact_id,
        linked_contact_name: selectedLinkContact ? `${selectedLinkContact.first_name} ${selectedLinkContact.last_name}` : t.linked_contact_name
      } : t));
      
    } catch (error) {
      toast.error('Failed to link email');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async () => {
    if (!selectedThread) return;
    
    try {
      await axios.post(`${API}/email/unlink`, { thread_id: selectedThread.id });
      toast.success('Email unlinked');
      
      setSelectedThread(prev => ({
        ...prev,
        linked_contact_id: null,
        linked_contact_name: null,
        linked_job_id: null,
        linked_job_title: null,
        link_confirmed: false
      }));
      
      setThreads(prev => prev.map(t => t.id === selectedThread.id ? {
        ...t,
        linked_contact_id: null,
        linked_contact_name: null
      } : t));
      
    } catch (error) {
      toast.error('Failed to unlink email');
    }
  };

  const handleAutoLink = async () => {
    if (!selectedThread) return;
    
    try {
      const res = await axios.post(`${API}/email/auto-link/${selectedThread.id}`);
      if (res.data.contact_linked || res.data.job_linked) {
        toast.success('Email auto-linked successfully');
        setSelectedThread(prev => ({
          ...prev,
          linked_contact_id: res.data.contact_id,
          linked_contact_name: res.data.contact_name,
          linked_job_id: res.data.job_id,
          linked_job_title: res.data.job_title
        }));
      } else {
        toast.info('No matching contact or job found');
      }
    } catch (error) {
      toast.error('Auto-link failed');
    }
  };

  // ========== AI Draft Functions ==========
  
  const fetchPendingDrafts = async () => {
    try {
      const res = await axios.get(`${API}/prime/email/pending-drafts`);
      setPendingDrafts(res.data.drafts || []);
    } catch (error) {
      console.error('Error fetching pending drafts:', error);
    }
  };

  const openAIDraftModal = () => {
    setShowAIDraftModal(true);
    setAiDraftInstructions('');
    setAiDraftTone('professional');
    setAiDraft(null);
  };

  const generateAIDraft = async () => {
    if (!selectedThread || !accounts.length) return;
    
    setGeneratingDraft(true);
    try {
      const res = await axios.post(`${API}/prime/email/draft`, {
        thread_id: selectedThread.id,
        account_id: selectedThread.account_id || accounts[0]?.id,
        instructions: aiDraftInstructions || null,
        tone: aiDraftTone,
        include_signature: true
      });
      
      if (res.data.success) {
        setAiDraft(res.data.draft);
        toast.success('AI draft generated!');
        fetchPendingDrafts();
      } else {
        toast.error(res.data.error || 'Failed to generate draft');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate draft');
    } finally {
      setGeneratingDraft(false);
    }
  };

  const approveDraft = async (draftId, modifications = null) => {
    try {
      await axios.post(`${API}/prime/email/approve`, {
        draft_id: draftId,
        modifications: modifications
      });
      toast.success('Draft approved and ready to send');
      setAiDraft(null);
      setShowAIDraftModal(false);
      fetchPendingDrafts();
    } catch (error) {
      toast.error('Failed to approve draft');
    }
  };

  const rejectDraft = async (draftId, reason = null) => {
    try {
      await axios.post(`${API}/prime/email/reject`, {
        draft_id: draftId,
        reason: reason
      });
      toast.info('Draft rejected');
      setAiDraft(null);
      fetchPendingDrafts();
    } catch (error) {
      toast.error('Failed to reject draft');
    }
  };

  const summarizeThread = async () => {
    if (!selectedThread) return;
    
    try {
      const res = await axios.post(`${API}/prime/email/summarize/${selectedThread.id}`);
      if (res.data.success) {
        toast.success('Summary generated');
        setSelectedThread(prev => ({ ...prev, ai_summary: res.data.summary }));
      }
    } catch (error) {
      toast.error('Failed to generate summary');
    }
  };

  // Fetch pending drafts on load
  useEffect(() => {
    fetchPendingDrafts();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="h-[calc(100vh-64px)] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-900/20 to-purple-900/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl">
              <Mail className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Email Hub</h1>
              <p className="text-sm text-muted-foreground">
                {stats.accounts || 0} accounts • {stats.unread_threads || 0} unread
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pendingDrafts.length > 0 && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowPendingDrafts(true)}
                className="border-amber-500 text-amber-600"
              >
                <Bot className="h-4 w-4 mr-2" />
                {pendingDrafts.length} AI Draft{pendingDrafts.length > 1 ? 's' : ''} Pending
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              Sync
            </Button>
            <Button onClick={() => setShowComposeModal(true)} className="bg-gradient-to-r from-blue-600 to-purple-600">
              <MailPlus className="h-4 w-4 mr-2" />
              Compose
            </Button>
          </div>
        </div>

        {/* Main 3-panel layout */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left sidebar */}
          <div className="w-64 border-r bg-slate-50/50 dark:bg-slate-900/50 flex flex-col">
            <div className="p-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Folders</h3>
              <div className="space-y-1">
                {['INBOX', 'SENT', 'DRAFTS', 'STARRED', 'TRASH'].map(folder => (
                  <button
                    key={folder}
                    onClick={() => setSelectedFolder(folder)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedFolder === folder ? 'bg-primary text-primary-foreground' : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    {FOLDER_ICONS[folder] || <Folder className="h-4 w-4" />}
                    <span className="flex-1 text-left">{folder.charAt(0) + folder.slice(1).toLowerCase()}</span>
                    {folder === 'INBOX' && stats.unread_threads > 0 && (
                      <Badge variant="destructive" className="h-5 px-1.5 text-xs">{stats.unread_threads}</Badge>
                    )}
                  </button>
                ))}
              </div>
            </div>
            <div className="border-t" />
            <div className="p-3 flex-1 overflow-auto">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase">Accounts</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowConnectModal(true)}>
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
              {accounts.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <Mail className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No accounts connected</p>
                  <Button variant="link" size="sm" onClick={() => setShowConnectModal(true)} className="mt-1">
                    Connect an account
                  </Button>
                </div>
              ) : (
                <div className="space-y-1">
                  <button
                    onClick={() => setSelectedAccount('all')}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedAccount === 'all' ? 'bg-slate-200 dark:bg-slate-700' : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    <span className="text-lg">📮</span>
                    <span className="flex-1 text-left">All Accounts</span>
                  </button>
                  {accounts.map(account => (
                    <div key={account.id} className="group relative">
                      <button
                        onClick={() => setSelectedAccount(account.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          selectedAccount === account.id ? 'bg-slate-200 dark:bg-slate-700' : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`}
                      >
                        <span className="text-lg">{PROVIDER_CONFIG[account.provider]?.icon || '📧'}</span>
                        <span className="flex-1 text-left truncate text-xs">{account.email_address}</span>
                      </button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDisconnect(account.id)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-3 border-t">
              <div className="flex gap-2">
                <Button variant={filterUnread ? 'default' : 'outline'} size="sm" onClick={() => setFilterUnread(!filterUnread)} className="flex-1 text-xs">Unread</Button>
                <Button variant={filterStarred ? 'default' : 'outline'} size="sm" onClick={() => setFilterStarred(!filterStarred)} className="flex-1 text-xs">
                  <Star className="h-3 w-3 mr-1" />Starred
                </Button>
              </div>
            </div>
          </div>

          {/* Middle - Thread list */}
          <div className="w-96 border-r flex flex-col">
            <div className="p-3 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search emails..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              {threads.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Inbox className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No emails found</p>
                  {accounts.length === 0 && <Button variant="link" onClick={() => setShowConnectModal(true)} className="mt-2">Connect an account</Button>}
                </div>
              ) : (
                <div className="divide-y">
                  {threads.map(thread => (
                    <button
                      key={thread.id}
                      onClick={() => fetchThread(thread.id)}
                      className={`w-full p-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800 ${
                        selectedThread?.id === thread.id ? 'bg-slate-100 dark:bg-slate-800' : ''
                      } ${thread.is_unread ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className={thread.is_unread ? 'bg-blue-100 text-blue-700' : ''}>
                            {getInitials(thread.participants?.[0]?.name, thread.participants?.[0]?.email)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className={`truncate ${thread.is_unread ? 'font-semibold' : ''}`}>
                              {thread.participants?.[0]?.name || thread.participants?.[0]?.email || 'Unknown'}
                            </span>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(thread.last_message_date)}</span>
                          </div>
                          <p className={`truncate text-sm ${thread.is_unread ? 'font-medium' : 'text-muted-foreground'}`}>{thread.subject}</p>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{thread.snippet}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {thread.is_starred && <Star className="h-3 w-3 text-amber-500 fill-amber-500" />}
                            {thread.message_count > 1 && <Badge variant="secondary" className="h-4 px-1 text-xs">{thread.message_count}</Badge>}
                            {thread.linked_contact_name && (
                              <Badge variant="outline" className="h-4 px-1 text-xs">
                                <User className="h-2 w-2 mr-1" />{thread.linked_contact_name}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right - Message viewer */}
          <div className="flex-1 flex flex-col bg-white dark:bg-slate-950">
            {loadingThread ? (
              <div className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
            ) : selectedThread ? (
              <>
                <div className="p-4 border-b">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-lg font-semibold">{selectedThread.subject}</h2>
                      <p className="text-sm text-muted-foreground mt-1">{selectedThread.message_count} message(s) • {selectedThread.account_email}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleStar(selectedThread.id, !selectedThread.is_starred)}>
                        <Star className={`h-4 w-4 ${selectedThread.is_starred ? 'fill-amber-500 text-amber-500' : ''}`} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleReply(selectedThread, threadMessages[threadMessages.length - 1])}>
                        <Reply className="h-4 w-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={openAIDraftModal}>
                            <Wand2 className="h-4 w-4 mr-2" />
                            AI Draft Reply
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={summarizeThread}>
                            <Sparkles className="h-4 w-4 mr-2" />
                            Summarize Thread
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={openLinkModal}>
                            <Link2 className="h-4 w-4 mr-2" />
                            Link to Contact/Job
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={handleAutoLink}>
                            <Bot className="h-4 w-4 mr-2" />
                            Auto-Link
                          </DropdownMenuItem>
                          {(selectedThread.linked_contact_id || selectedThread.linked_job_id) && (
                            <DropdownMenuItem onClick={handleUnlink} className="text-red-600">
                              <Unlink className="h-4 w-4 mr-2" />
                              Remove Link
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  
                  {/* Linked entities */}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {selectedThread.linked_contact_name && (
                      <Badge variant="outline" className="gap-1">
                        <User className="h-3 w-3" />{selectedThread.linked_contact_name}
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                      </Badge>
                    )}
                    {selectedThread.linked_job_title && (
                      <Badge variant="outline" className="gap-1 border-blue-500 text-blue-600">
                        <Briefcase className="h-3 w-3" />{selectedThread.linked_job_title}
                      </Badge>
                    )}
                    {!selectedThread.linked_contact_id && !selectedThread.linked_job_id && (
                      <Button variant="ghost" size="sm" className="h-6 text-xs text-muted-foreground" onClick={openLinkModal}>
                        <Link2 className="h-3 w-3 mr-1" />Link to Contact/Job
                      </Button>
                    )}
                  </div>
                  
                  {/* AI Summary */}
                  {selectedThread.ai_summary && (
                    <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <div className="flex items-center gap-2 text-xs font-medium text-purple-600 dark:text-purple-400 mb-1">
                        <Sparkles className="h-3 w-3" />AI Summary
                      </div>
                      <p className="text-sm">{selectedThread.ai_summary}</p>
                    </div>
                  )}
                </div>
                <div className="flex-1 overflow-auto p-4 space-y-4">
                  {threadMessages.map((message) => (
                    <Card key={message.id}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center gap-3">
                          <Avatar><AvatarFallback>{getInitials(message.from_participant?.name, message.from_participant?.email)}</AvatarFallback></Avatar>
                          <div className="flex-1">
                            <p className="font-medium">{message.from_participant?.name || message.from_participant?.email}</p>
                            <p className="text-xs text-muted-foreground">to {message.to_participants?.map(p => p.name || p.email).join(', ')}</p>
                          </div>
                          <span className="text-xs text-muted-foreground">{new Date(message.date).toLocaleString()}</span>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="prose prose-sm dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: message.body_html || `<p>${message.body_text || message.snippet}</p>` }} />
                        {message.has_attachments && message.attachments?.length > 0 && (
                          <div className="mt-4 pt-4 border-t flex flex-wrap gap-2">
                            {message.attachments.map(att => (
                              <div key={att.id} className="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-sm">
                                <Paperclip className="h-4 w-4 text-muted-foreground" />
                                <span className="truncate max-w-[150px]">{att.filename}</span>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0"><Download className="h-3 w-3" /></Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <div className="p-4 border-t bg-slate-50 dark:bg-slate-900">
                  <Button className="w-full" variant="outline" onClick={() => handleReply(selectedThread, threadMessages[threadMessages.length - 1])}>
                    <Reply className="h-4 w-4 mr-2" />Reply
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
                <Mail className="h-16 w-16 mb-4 opacity-30" />
                <p className="text-lg">Select an email to read</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Connect Modal */}
      <Dialog open={showConnectModal} onOpenChange={setShowConnectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5" />Connect Email Account</DialogTitle>
            <DialogDescription>Connect your email to sync messages</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Provider</Label>
              <Select value={connectForm.provider} onValueChange={(v) => setConnectForm({ ...connectForm, provider: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gmail">📧 Gmail</SelectItem>
                  <SelectItem value="microsoft">📨 Microsoft 365</SelectItem>
                  <SelectItem value="imap">📬 Other (IMAP)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Email Address</Label>
              <Input placeholder="you@example.com" value={connectForm.email} onChange={(e) => setConnectForm({ ...connectForm, email: e.target.value })} className="mt-1" />
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg text-sm">
              <p className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                <AlertCircle className="h-4 w-4" />OAuth is currently mocked. Real provider integration coming soon.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConnectModal(false)}>Cancel</Button>
            <Button onClick={handleConnect} className="bg-gradient-to-r from-blue-600 to-purple-600">Connect Account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compose Modal */}
      <Dialog open={showComposeModal} onOpenChange={setShowComposeModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MailPlus className="h-5 w-5" />{composeData.isReply ? 'Reply' : 'New Email'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {accounts.length > 1 && (
              <div>
                <Label>From</Label>
                <Select value={composeData.accountId || accounts[0]?.id} onValueChange={(v) => setComposeData({ ...composeData, accountId: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {accounts.map(acc => <SelectItem key={acc.id} value={acc.id}>{acc.email_address}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><Label>To</Label><Input placeholder="recipient@example.com" value={composeData.to} onChange={(e) => setComposeData({ ...composeData, to: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>CC</Label><Input placeholder="cc@example.com" value={composeData.cc} onChange={(e) => setComposeData({ ...composeData, cc: e.target.value })} className="mt-1" /></div>
              <div><Label>BCC</Label><Input placeholder="bcc@example.com" value={composeData.bcc} onChange={(e) => setComposeData({ ...composeData, bcc: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Subject</Label><Input placeholder="Subject" value={composeData.subject} onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })} className="mt-1" /></div>
            <div><Label>Message</Label><Textarea placeholder="Write your message..." value={composeData.body} onChange={(e) => setComposeData({ ...composeData, body: e.target.value })} className="mt-1 min-h-[200px]" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComposeModal(false)}>Cancel</Button>
            <Button onClick={handleSend} disabled={sending} className="bg-gradient-to-r from-blue-600 to-purple-600">
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link to Contact/Job Modal */}
      <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Link Email to Contact/Job
            </DialogTitle>
            <DialogDescription>
              Connect this email thread to a contact or job for better organization
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Search Contacts/Jobs</Label>
              <Input 
                placeholder="Search by name or email..." 
                value={linkSearch} 
                onChange={(e) => {
                  setLinkSearch(e.target.value);
                  fetchLinkableContacts(e.target.value);
                  fetchLinkableJobs(null, e.target.value);
                }} 
                className="mt-1" 
              />
            </div>
            
            <div>
              <Label className="text-sm text-muted-foreground">Select Contact</Label>
              <ScrollArea className="h-32 mt-1 border rounded-md">
                <div className="p-2 space-y-1">
                  {linkableContacts.map(contact => (
                    <button
                      key={contact.id}
                      onClick={() => {
                        setSelectedLinkContact(contact);
                        fetchLinkableJobs(contact.id);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                        selectedLinkContact?.id === contact.id 
                          ? 'bg-blue-100 dark:bg-blue-900/50 border border-blue-500' 
                          : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span>{contact.first_name} {contact.last_name}</span>
                      <span className="text-xs text-muted-foreground ml-auto">{contact.email}</span>
                    </button>
                  ))}
                  {linkableContacts.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">No contacts found</p>
                  )}
                </div>
              </ScrollArea>
            </div>
            
            <div>
              <Label className="text-sm text-muted-foreground">Select Job (Optional)</Label>
              <ScrollArea className="h-32 mt-1 border rounded-md">
                <div className="p-2 space-y-1">
                  {linkableJobs.map(job => (
                    <button
                      key={job.id}
                      onClick={() => setSelectedLinkJob(job)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                        selectedLinkJob?.id === job.id 
                          ? 'bg-blue-100 dark:bg-blue-900/50 border border-blue-500' 
                          : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      <Briefcase className="h-4 w-4 text-muted-foreground" />
                      <span className="truncate">{job.title}</span>
                      <Badge variant="secondary" className="ml-auto text-xs">{job.status}</Badge>
                    </button>
                  ))}
                  {linkableJobs.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">No jobs found</p>
                  )}
                </div>
              </ScrollArea>
            </div>
            
            {(selectedLinkContact || selectedLinkJob) && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <p className="text-sm font-medium text-green-700 dark:text-green-400 mb-1">Selected:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedLinkContact && (
                    <Badge variant="outline" className="gap-1">
                      <User className="h-3 w-3" />
                      {selectedLinkContact.first_name} {selectedLinkContact.last_name}
                    </Badge>
                  )}
                  {selectedLinkJob && (
                    <Badge variant="outline" className="gap-1 border-blue-500 text-blue-600">
                      <Briefcase className="h-3 w-3" />
                      {selectedLinkJob.title}
                    </Badge>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkModal(false)}>Cancel</Button>
            <Button 
              onClick={handleManualLink} 
              disabled={linking || (!selectedLinkContact && !selectedLinkJob)}
              className="bg-gradient-to-r from-blue-600 to-purple-600"
            >
              {linking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
              Link Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Draft Modal */}
      <Dialog open={showAIDraftModal} onOpenChange={setShowAIDraftModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-purple-500" />
              AI Draft Reply
            </DialogTitle>
            <DialogDescription>
              Let Prime AI generate a draft response. All drafts require your approval before sending.
            </DialogDescription>
          </DialogHeader>
          
          {!aiDraft ? (
            <div className="space-y-4 py-4">
              <div>
                <Label>Tone</Label>
                <Select value={aiDraftTone} onValueChange={setAiDraftTone}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="friendly">Friendly</SelectItem>
                    <SelectItem value="formal">Formal</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Special Instructions (Optional)</Label>
                <Textarea 
                  placeholder="E.g., 'Focus on scheduling a follow-up call' or 'Include pricing information'"
                  value={aiDraftInstructions}
                  onChange={(e) => setAiDraftInstructions(e.target.value)}
                  className="mt-1"
                  rows={3}
                />
              </div>
              
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-700 dark:text-amber-300">Draft-First Policy</p>
                    <p className="text-amber-600 dark:text-amber-400">
                      AI drafts are saved for your review. You must approve before sending.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Bot className="h-4 w-4 text-purple-500" />
                  <span className="text-sm font-medium text-purple-700 dark:text-purple-300">AI Generated Draft</span>
                  <Badge variant="secondary" className="ml-auto">{aiDraft.status}</Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-1"><strong>To:</strong> {aiDraft.to?.join(', ')}</p>
                <p className="text-sm text-muted-foreground mb-2"><strong>Subject:</strong> {aiDraft.subject}</p>
              </div>
              
              <div>
                <Label>Draft Content</Label>
                <Textarea 
                  value={aiDraft.body_text}
                  onChange={(e) => setAiDraft(prev => ({ ...prev, body_text: e.target.value }))}
                  className="mt-1 min-h-[200px]"
                />
                <p className="text-xs text-muted-foreground mt-1">You can edit this draft before approving</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            {!aiDraft ? (
              <>
                <Button variant="outline" onClick={() => setShowAIDraftModal(false)}>Cancel</Button>
                <Button 
                  onClick={generateAIDraft} 
                  disabled={generatingDraft}
                  className="bg-gradient-to-r from-purple-600 to-blue-600"
                >
                  {generatingDraft ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
                  ) : (
                    <><Wand2 className="h-4 w-4 mr-2" />Generate Draft</>
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => rejectDraft(aiDraft.id)}
                  className="text-red-600"
                >
                  <ThumbsDown className="h-4 w-4 mr-2" />Reject
                </Button>
                <Button 
                  onClick={() => approveDraft(aiDraft.id, { body_text: aiDraft.body_text })}
                  className="bg-gradient-to-r from-green-600 to-emerald-600"
                >
                  <ThumbsUp className="h-4 w-4 mr-2" />Approve & Send
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending AI Drafts Modal */}
      <Dialog open={showPendingDrafts} onOpenChange={setShowPendingDrafts}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-amber-500" />
              Pending AI Drafts
            </DialogTitle>
            <DialogDescription>
              Review and approve AI-generated email drafts
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-3 py-4">
              {pendingDrafts.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No pending drafts</p>
              ) : (
                pendingDrafts.map(draft => (
                  <Card key={draft.id} className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium">{draft.subject}</p>
                        <p className="text-sm text-muted-foreground">To: {draft.to?.join(', ')}</p>
                      </div>
                      <Badge variant="secondary">{draft.status}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{draft.body_text}</p>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => rejectDraft(draft.id)}
                        className="text-red-600"
                      >
                        <ThumbsDown className="h-3 w-3 mr-1" />Reject
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => approveDraft(draft.id)}
                        className="bg-green-600"
                      >
                        <ThumbsUp className="h-3 w-3 mr-1" />Approve
                      </Button>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPendingDrafts(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default EmailHub;
